import type { FC, ReactNode } from 'react';
import React, { useCallback, useState, useMemo } from 'react';

export const ASC = 'asc';
export type SortOrderType = 'asc' | 'desc';

export type CharlieTableProviderProps = {
	children?: ReactNode;
	defaultSortOrder?: SortOrderType;
	defaultPageSize?: number;
	showPageSelector?: boolean;
};

export type CharlieTableContextType = {
	pushPageCursor: (cursor: string) => void;
	popPageCursor: () => void;
	resetPageCursors: () => void;
	setSortOrder: React.Dispatch<React.SetStateAction<SortOrderType>>;
	updatePageSize: (newPageSize: number) => void;
	pageSize: number;
	currentPage: number;
	pageCursor: string;
	sortOrder: SortOrderType;
};

export const CharlieTableContext = React.createContext<CharlieTableContextType>({
	pushPageCursor: () => {},
	popPageCursor: () => {},
	resetPageCursors: () => {},
	setSortOrder: () => {},
	updatePageSize: () => {},
	currentPage: 1,
	pageCursor: '',
	sortOrder: ASC,
	pageSize: 20,
});

export const CharlieTableContextProvider: FC<CharlieTableProviderProps> = ({
	children,
	defaultSortOrder = ASC,
	defaultPageSize = 20,
}) => {
	const [pageCursors, setPageCursors] = useState<string[]>(['']);
	const [sortOrder, setSortOrder] = useState<SortOrderType>(defaultSortOrder);
	const [pageSize, setPageSize] = useState<number>(defaultPageSize);

	const pushPageCursor = useCallback(
		(cursor: string) => {
			setPageCursors((cursors) => {
				if (cursors[cursors.length - 1] !== cursor) {
					return [...cursors, cursor];
				} else {
					return cursors;
				}
			});
		},
		[setPageCursors],
	);

	const popPageCursor = useCallback(() => {
		if (pageCursors.length > 1) {
			setPageCursors((cursors) => cursors.slice(0, -1));
		}
	}, [pageCursors]);

	const resetPageCursors = useCallback(() => {
		setPageCursors(['']);
	}, []);

	const updatePageSize = useCallback((newPageSize: number) => {
		setPageSize(newPageSize);
	}, []);

	const charlieTableContextValue = useMemo(
		() => ({
			pushPageCursor,
			popPageCursor,
			resetPageCursors,
			setSortOrder,
			currentPage: pageCursors.length,
			pageCursor: pageCursors[pageCursors.length - 1],
			sortOrder,
			pageSize,
			updatePageSize,
		}),
		[
			pushPageCursor,
			popPageCursor,
			resetPageCursors,
			pageCursors,
			sortOrder,
			pageSize,
			updatePageSize,
		],
	);

	return (
		<CharlieTableContext.Provider value={charlieTableContextValue}>
			{children}
		</CharlieTableContext.Provider>
	);
};
